/*
 * Description: Display Code Web Component
 * Purpose: Displays a code block with copy-to-clipboard functionality
 * Author: Nuvemx
 * Created: 2026
 *
 * Features:
 * - Syntax-highlighted-ready code block
 * - Copy to clipboard button
 * - Language label display
 * - Custom event on copy
 */

import './display_code.css';

class display_code extends HTMLElement {
    constructor() {
        super();
        this._onCopy = () => this.copyToClipboard();
    }

    connectedCallback() {
        if (this.dataset.rendered) {
            const button = this.querySelector('.copy-btn');
            if (button) {
                button.addEventListener('click', this._onCopy);
            }
            return;
        }

        const language = this.getAttribute('language') || '';
        // Preserve the original text before clearing the innerHTML
        const originalCode = this.textContent.trim();
        
        this.innerHTML = `
            <div class="code-block position-relative mb-3" > 
                <div class="code-header d-flex justify-content-between align-items-center bg-light text-dark border-bottom px-3 py-1 rounded-top">
                    <span class="code-language">${language}</span>
                    <button type="button" class="copy-btn btn btn-sm btn-outline-secondary">Copy</button>
                </div>
                <pre class="bg-white text-dark p-3 rounded-bottom mb-0 border overflow-auto"><code>${this._escapeHtml(originalCode)}</code></pre>
            </div>`;
        this.dataset.rendered = "true";

        const button = this.querySelector('.copy-btn');
        if (button) {
            button.addEventListener('click', this._onCopy);
        }
    }

    disconnectedCallback() {
        const button = this.querySelector('.copy-btn');
        if (button) {
            button.removeEventListener('click', this._onCopy);
        }
    }

    copyToClipboard() {
        const codeEl = this.querySelector('code');
        if (!codeEl) return;

        const text = codeEl.textContent;

        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(text)
                .then(() => this._onCopySuccess())
                .catch(() => this._fallbackCopy(text));
        } else {
            this._fallbackCopy(text);
        }
    }

    _fallbackCopy(text) {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        try {
            document.execCommand('copy');
            this._onCopySuccess();
        } catch (e) {
            console.error('display_code: Copy failed', e);
        }
        document.body.removeChild(textarea);
    }

    _onCopySuccess() {
        const button = this.querySelector('.copy-btn');
        if (button) {
            const originalText = button.textContent;
            button.innerHTML = '<i class="bx bx-check"></i> Copied!';
            button.disabled = true;
            setTimeout(() => {
                button.textContent = originalText;
                button.disabled = false;
            }, 2000);
        }

        this.dispatchEvent(new CustomEvent('code-copied', {
            bubbles: true,
            composed: true,
            detail: { code: this.querySelector('code')?.textContent || '' }
        }));
    }

    _escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }
}

export { display_code };

